import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { RichUtils } from 'draft-js';

function ToolbarDropdown({
  editorState,
  onChange,
  size = 'default',
  className
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const toggleDropdown = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const applyInlineStyle = (e, style) => {
    e.preventDefault();
    e.stopPropagation();

    // Force selection to maintain focus
    const editorStateFocused = RichUtils.toggleInlineStyle(editorState, style);
    onChange(editorStateFocused);
    setIsOpen(false);
  };

  const handleLinkClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Find and click the hidden link button from the toolbar
    const linkButton = document.querySelector(
      '.rdw-option-wrapper[title="Link"]'
    );
    if (linkButton) {
      linkButton.click();
    }

    setIsOpen(false);
  };

  const handleImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Find and click the hidden image button from the toolbar
    const imageButton = document.querySelector(
      '.rdw-option-wrapper[title="Image"]'
    );
    if (imageButton) {
      imageButton.click();
    }

    setIsOpen(false);
  };

  const svgSize =
    size === 'small' ? { width: 12, height: 11 } : { width: 18, height: 16 };
  const circleRadius = size === 'small' ? 1.4 : 2;

  return (
    <Container ref={dropdownRef} className={className}>
      <DropdownButton
        onMouseDown={toggleDropdown}
        $isOpen={isOpen}
        $size={size}>
        <svg
          width={svgSize.width}
          height={svgSize.height}
          viewBox="0 0 16 16"
          fill="none">
          <circle cx="1" cy="8" r={circleRadius} fill="white" />
          <circle cx="8" cy="8" r={circleRadius} fill="white" />
          <circle cx="15" cy="8" r={circleRadius} fill="white" />
        </svg>
      </DropdownButton>
      {isOpen && (
        <DropdownMenu>
          <MenuItem onMouseDown={(e) => applyInlineStyle(e, 'BOLD')}>
            Bold
          </MenuItem>
          <MenuItem onMouseDown={(e) => applyInlineStyle(e, 'ITALIC')}>
            Italic
          </MenuItem>
          <MenuItem onMouseDown={(e) => applyInlineStyle(e, 'UNDERLINE')}>
            Underline
          </MenuItem>
          <Divider />
          <MenuItem onMouseDown={handleLinkClick}>Link</MenuItem>
          <MenuItem onMouseDown={handleImageClick}>Image</MenuItem>
        </DropdownMenu>
      )}
    </Container>
  );
}

const Container = styled.div`
  bottom: 0px;
  left: 0px;
  position: absolute;
  z-index: 3;
`;

const DropdownButton = styled.button`
  align-items: center;
  background-color: ${({ $isOpen }) =>
    $isOpen ? 'var(--dark-blue)' : 'var(--blue)'};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: ${({ $size }) => ($size === 'small' ? '20px' : '35px')};
  justify-content: center;
  min-width: unset;
  padding: 2px;
  transition: background-color 0.2s;
  width: ${({ $size }) => ($size === 'small' ? '20px' : '35px')};

  &:hover {
    background-color: var(--dark-blue);
  }

  &:focus {
    outline: none;
  }
`;

const DropdownMenu = styled.div`
  background: white;
  border: 1px solid var(--light-grey);
  border-radius: var(--border-radius-sm);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  left: 0;
  min-width: 120px;
  overflow: hidden;
  position: absolute;
  top: calc(100% + 4px);
  z-index: 1000;
`;

const MenuItem = styled.div`
  color: var(--dark-blue);
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  padding: 8px 16px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(226, 228, 234, 0.4);
  }

  &:active {
    background-color: rgba(226, 228, 234, 0.6);
  }
`;

const Divider = styled.div`
  background-color: var(--light-grey);
  height: 1px;
  margin: 4px 0;
`;

export default ToolbarDropdown;
